import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, Icon, FormControl, FormLabel, Input, Textarea, Button, SimpleGrid, Select } from "@chakra-ui/react";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaArrowRight } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

const PropertyContact: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/contact"
        fallbackTitle="Contact One Property Australia"
        fallbackDescription="Contact our property investment specialists for a free strategy consultation"
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 24 }}
        backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Heading size="3xl" fontWeight="black">
              Contact Our Property Team
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Get in touch with our property investment specialists for a free, no-obligation strategy consultation
            </Text>
            <Button
              size="lg"
              bg={PROPERTY_PRIMARY}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#0a6459" }}
              transition="all 0.2s"
              mt={2}
            >
              Book a Free Consultation
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Contact Form & Info */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
          {/* Contact Info */}
          <VStack spacing={6} align="stretch">
            <Heading size="lg" color={NAVY}>Get In Touch</Heading>
            <Text color="gray.600">
              Our property investment specialists are ready to help you build retirement wealth beyond superannuation. Contact us today for a free strategy consultation.
            </Text>

            <Box p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaPhone} color={PROPERTY_PRIMARY} boxSize={5} />
                <Heading size="sm" color={NAVY}>Phone</Heading>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">1300 PROPERTY</Text>
              <Text fontSize="sm" color="gray.600">Mon–Fri 9am–5pm AEST</Text>
            </Box>

            <Box p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaEnvelope} color={PROPERTY_PRIMARY} boxSize={5} />
                <Heading size="sm" color={NAVY}>Email</Heading>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">hello@onepropertyaustralia.com.au</Text>
              <Text fontSize="sm" color="gray.600">We respond within 24 hours</Text>
            </Box>

            <Box p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaMapMarkerAlt} color={PROPERTY_PRIMARY} boxSize={5} />
                <Heading size="sm" color={NAVY}>Office</Heading>
              </HStack>
              <Text fontWeight="bold">Australia Wide</Text>
              <Text fontSize="sm" color="gray.600">Servicing all major cities and regional areas</Text>
            </Box>

            <Box p={6} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaClock} color={PROPERTY_PRIMARY} boxSize={5} />
                <Heading size="sm" color={NAVY}>Business Hours</Heading>
              </HStack>
              <VStack align="start" spacing={1} fontSize="sm" color="gray.700">
                <Text><strong>Monday – Friday:</strong> 9:00am – 5:00pm</Text>
                <Text><strong>Saturday:</strong> By appointment only</Text>
                <Text><strong>Sunday:</strong> Closed</Text>
              </VStack>
            </Box>
          </VStack>

          {/* Contact Form */}
          <Box bg="gray.50" p={8} borderRadius="2xl">
            <Heading size="lg" color={NAVY} mb={6}>Send Us a Message</Heading>
            <VStack spacing={4}>
              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input bg="white" placeholder="John" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input bg="white" placeholder="Smith" />
                </FormControl>
              </SimpleGrid>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input bg="white" type="email" placeholder="john@example.com" />
              </FormControl>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input bg="white" type="tel" placeholder="04XX XXX XXX" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Service Interest</FormLabel>
                <Select bg="white" placeholder="Select service">
                  <option>Investment Strategy</option>
                  <option>OPA+ Off-Market Access</option>
                  <option>Investment Financing</option>
                  <option>Tax Protection</option>
                  <option>Property + Solar Package</option>
                  <option>General Inquiry</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Message</FormLabel>
                <Textarea bg="white" rows={5} placeholder="Tell us about your property investment goals..." />
              </FormControl>
              <Button
                bg={PROPERTY_PRIMARY}
                color="white"
                size="lg"
                width="full"
                _hover={{ bg: "#0a6459" }}
                transition="all 0.2s"
              >
                Send Message
              </Button>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                By submitting this form, you agree to our privacy policy and consent to being contacted by our team.
              </Text>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Ready to Build Your Property Portfolio?</Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              Join thousands of Australians who have secured their retirement with data-driven property investment strategies
            </Text>
            <Button
              size="lg"
              bg={PROPERTY_PRIMARY}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#0a6459" }}
              transition="all 0.2s"
            >
              Book a Free Consultation
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyContact;
